import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Crown, Palette, Eye, RefreshCw, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';

const VendorShopTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [isGold, setIsGold] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [customColors, setCustomColors] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch all templates
      const templatesRes = await axiosInstance.get('/shop-templates/');
      setTemplates(templatesRes.data.templates || []);

      // Fetch current template
      const currentRes = await axiosInstance.get('/shop-templates/my-template');
      setCurrentTemplate(currentRes.data);
      setIsGold(currentRes.data.is_gold);
      setCustomColors(currentRes.data.custom_colors);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTemplate = async (templateId) => {
    setApplying(true);
    try {
      await axiosInstance.post('/shop-templates/apply', {
        template_id: templateId,
        custom_colors: templateId === currentTemplate?.template_id ? customColors : null
      });
      toast.success('Vorlage wurde angewendet!');
      fetchData();
      setPreviewTemplate(null);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Anwenden der Vorlage';
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  };

  const handleResetTemplate = async () => {
    setApplying(true);
    try {
      await axiosInstance.post('/shop-templates/reset');
      toast.success('Zurück zur Standard-Vorlage');
      setCustomColors(null);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    } finally {
      setApplying(false);
    }
  };

  const handleSaveCustomColors = async () => {
    if (!isGold) {
      toast.error('Individuelle Farben sind nur für Gold-Verkäufer verfügbar');
      return;
    }

    setApplying(true);
    try {
      await axiosInstance.post('/shop-templates/apply', {
        template_id: currentTemplate?.template_id || 'homeyoursoul',
        custom_colors: customColors
      });
      toast.success('Farben gespeichert!');
      setShowColorPicker(false);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Speichern der Farben');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5EDE4' }}>
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-[#8B7355] border-t-transparent mx-auto mb-2"></div>
          <p className="text-[#5D4E3C]">Lade Vorlagen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5EDE4' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B7355] to-[#6D5A47] text-white px-4 py-6">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/vendor-dashboard')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Zurück zum Dashboard
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Palette className="h-6 w-6" />
            Shop-Design-Vorlagen
          </h1>
          <p className="text-white/80 mt-1">
            Wählen Sie ein Design für Ihren Shop
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Gold Badge Info */}
        {!isGold && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 p-4 mb-6">
            <div className="flex items-start gap-3">
              <Crown className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800">Gold-Verkäufer Vorteile</h3>
                <p className="text-amber-700 text-sm mt-1">
                  Mit Gold erhalten Sie Zugang zu allen Premium-Vorlagen und können individuelle Farben anpassen.
                </p>
                <Button
                  variant="outline"
                  className="mt-3 border-amber-400 text-amber-700 hover:bg-amber-100"
                  onClick={() => navigate('/become-vendor')}
                >
                  Zu Gold upgraden
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Current Template */}
        <div className="bg-white p-6 mb-6 shadow-sm">
          <h2 className="text-lg font-semibold text-[#5D4E3C] mb-4">Aktuelle Vorlage</h2>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div 
                className="w-16 h-16 flex items-center justify-center"
                style={{ 
                  backgroundColor: currentTemplate?.template?.colors?.primary || '#C4A084'
                }}
              >
                <Palette className="h-8 w-8 text-white" />
              </div>
              <div>
                <p className="font-medium text-[#5D4E3C]">
                  {currentTemplate?.template?.name || 'Homeyoursoul Vorlage'}
                </p>
                <p className="text-sm text-[#8B7355]">
                  {currentTemplate?.template?.description}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {isGold && (
                <Button
                  variant="outline"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  className="border-[#C4A084] text-[#5D4E3C]"
                >
                  <Palette className="h-4 w-4 mr-2" />
                  Farben anpassen
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleResetTemplate}
                disabled={applying || currentTemplate?.template_id === 'homeyoursoul'}
                className="border-[#C4A084] text-[#5D4E3C]"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Zurücksetzen
              </Button>
            </div>
          </div>

          {/* Color Picker Panel */}
          {showColorPicker && isGold && (
            <div className="mt-6 pt-6 border-t border-[#D4C4B0]">
              <h3 className="font-medium text-[#5D4E3C] mb-4">Individuelle Farben</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['primary', 'secondary', 'accent', 'background'].map((colorKey) => (
                  <div key={colorKey}>
                    <label className="block text-sm text-[#8B7355] mb-1 capitalize">
                      {colorKey === 'primary' ? 'Primärfarbe' :
                       colorKey === 'secondary' ? 'Sekundärfarbe' :
                       colorKey === 'accent' ? 'Akzentfarbe' : 'Hintergrund'}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={customColors?.[colorKey] || currentTemplate?.template?.colors?.[colorKey] || '#C4A084'}
                        onChange={(e) => setCustomColors({
                          ...customColors,
                          [colorKey]: e.target.value
                        })}
                        className="w-10 h-10 cursor-pointer border border-[#D4C4B0]"
                      />
                      <input
                        type="text"
                        value={customColors?.[colorKey] || currentTemplate?.template?.colors?.[colorKey] || ''}
                        onChange={(e) => setCustomColors({
                          ...customColors,
                          [colorKey]: e.target.value
                        })}
                        className="flex-1 px-2 py-1 border border-[#D4C4B0] text-sm"
                        placeholder="#C4A084"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleSaveCustomColors} disabled={applying}>
                  Farben speichern
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setCustomColors(null);
                    setShowColorPicker(false);
                  }}
                  className="border-[#C4A084] text-[#5D4E3C]"
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Templates Grid */}
        <h2 className="text-lg font-semibold text-[#5D4E3C] mb-4">Alle Vorlagen</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => {
            const isActive = currentTemplate?.template_id === template.id;
            const isPremium = template.is_premium && !isGold;
            
            return (
              <div
                key={template.id}
                className={`bg-white overflow-hidden shadow-sm transition-all ${
                  isActive ? 'ring-2 ring-[#C4A084]' : ''
                } ${isPremium ? 'opacity-75' : 'hover:shadow-md'}`}
              >
                {/* Preview */}
                <div 
                  className="h-40 relative"
                  style={{ 
                    background: `linear-gradient(135deg, ${template.colors.primary} 0%, ${template.colors.secondary} 100%)`
                  }}
                >
                  {isActive && (
                    <div className="absolute top-3 right-3 bg-white text-[#8B7355] px-3 py-1 text-sm font-medium flex items-center gap-1">
                      <Check className="h-4 w-4" />
                      Aktiv
                    </div>
                  )}
                  {isPremium && (
                    <div className="absolute top-3 left-3 bg-amber-500 text-white px-3 py-1 text-sm font-medium flex items-center gap-1">
                      <Crown className="h-4 w-4" />
                      Gold
                    </div>
                  )}
                  
                  {/* Mini Preview Elements */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/90 p-3">
                      <div 
                        className="h-2 w-20 mb-2"
                        style={{ backgroundColor: template.colors.text }}
                      ></div>
                      <div className="flex gap-2">
                        <div 
                          className="h-6 px-3 flex items-center text-xs text-white"
                          style={{ backgroundColor: template.colors.primary }}
                        >
                          Button
                        </div>
                        <div 
                          className="h-6 px-3 flex items-center text-xs border"
                          style={{ 
                            borderColor: template.colors.border,
                            color: template.colors.text
                          }}
                        >
                          Outline
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-[#5D4E3C] flex items-center gap-2">
                    {template.name}
                    {template.is_default && (
                      <span className="text-xs bg-[#F5EDE4] text-[#8B7355] px-2 py-0.5">
                        Standard
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-[#8B7355] mt-1 line-clamp-2">
                    {template.description}
                  </p>

                  {/* Color Preview */}
                  <div className="flex gap-1 mt-3">
                    {Object.entries(template.colors).slice(0, 5).map(([key, color]) => (
                      <div
                        key={key}
                        className="w-6 h-6 border border-gray-200"
                        style={{ backgroundColor: color }}
                        title={key}
                      />
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    {isPremium ? (
                      <Button
                        variant="outline"
                        className="flex-1 border-amber-400 text-amber-700"
                        onClick={() => navigate('/become-vendor')}
                      >
                        <Lock className="h-4 w-4 mr-2" />
                        Gold erforderlich
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          className="flex-1 border-[#C4A084] text-[#5D4E3C]"
                          onClick={() => setPreviewTemplate(previewTemplate === template.id ? null : template.id)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Vorschau
                        </Button>
                        <Button
                          className="flex-1"
                          onClick={() => handleApplyTemplate(template.id)}
                          disabled={applying || isActive}
                        >
                          {isActive ? (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              Aktiv
                            </>
                          ) : (
                            'Anwenden'
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Extended Preview */}
                {previewTemplate === template.id && (
                  <div 
                    className="p-4 border-t"
                    style={{ backgroundColor: template.colors.background }}
                  >
                    <p className="text-xs mb-2" style={{ color: template.colors.text_light }}>
                      Vorschau
                    </p>
                    <div 
                      className="p-3 mb-2"
                      style={{ backgroundColor: template.colors.card_bg, border: `1px solid ${template.colors.border}` }}
                    >
                      <p className="font-medium mb-1" style={{ color: template.colors.text }}>
                        Beispiel Produkt
                      </p>
                      <p className="text-sm mb-2" style={{ color: template.colors.text_light }}>
                        Produktbeschreibung hier
                      </p>
                      <span 
                        className="inline-block px-3 py-1 text-white text-sm"
                        style={{ backgroundColor: template.colors.primary }}
                      >
                        12,99€
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VendorShopTemplates;
